import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { DefaultJsonForm } from '../../../components/v2/default-json-form'
import TabGeneral from './tab-general'
import TableCallLog from './table-call-log'
import TableContacts from './table-contacts'
import { Form, Alert, Skeleton, Button, Tabs, Space, Modal, TabsProps } from 'antd'
import { SaveOutlined, PlusOutlined } from '@ant-design/icons'
import { MapContent, MapTitle, MapWrapper, AlertWrapper, TaskWrapper } from './styles'
import defaultSelectedSchema from './json-schema.json'
import { setEnumFields } from '../../utils'
import { MargeModal } from './components/marge-modal'
import TabIndividual from './tab-individual'
import LegalTab from './tab-legal'

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

const breadcrumbs = [
  {
    href: '/',
    title: 'Контактный центр',
  },
  {
    href: `/call-center/new-clients`,
    title: 'Список клиентов',
  },
]

function setDisabledFields(data, schema) {
  const disabledFields = data.filter(obj => obj.disabled === true)
  disabledFields.forEach(obj => {
    if (schema.properties[obj.name]) {
      schema.properties[obj.name].options = {...schema.properties[obj.name].options, disabled: true}
    }
  })
}

export default function AdminNewObjectPage() {
  const { id } = useParams() as any
  const navigate = useNavigate()
  const [isDataLoading, setIsDataLoading] = useState<Boolean>(true)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [pageSuccess, setPageSuccess] = useState<string[]>([])
  const formDataRef = useRef<any>({})
  const [form] = Form.useForm()
  const [matchedJSONSchema, setMatchedJSONSchema] = useState<any>(null)
  const [clientType, setClientType] = useState<null | 'legal' | 'individual'>(null)

  const saveClientData = useCallback(
    async () => {
      const formData = { ...formDataRef.current }
      if (clientType === 'individual') {
        formData.legal = undefined
      }
      if (clientType === 'legal') {
        formData.individual = undefined
      }

      await fetchAPI(`/api/customers/${id}`, {
        method: 'POST', body: JSON.stringify(formData)
      }).then(result => {
        if (result?.error) {
          const errors =
            Array.isArray(result['errors']) && result['errors']?.length > 0
              ? result['errors'].map((error) => error['detail'])
              : ['Неизвестная ошибка при выполнении запроса']
          setPageSuccess([])
          setPageErrors(errors)
        } else {
          setPageSuccess(['Запись успешно обновлена.'])
          setPageErrors([])
        }
      })
    },
    [id, clientType],
  )

  const getClientType = (type?:number) => {
    if (!type || type === 4586) return null
    if (type === 306) return 'individual'
    return 'legal'
  }

  const getData = useCallback(async () => {
    {
      const [clientData, enums, additionalData] = await Promise.all([
        fetchAPI(`/api/customers/${id}`),
        fetchAPI('/api/enum/customers'),
        fetchAPI(`/api/customer/additional_data/${id}`),
      ])
      const formData = clientData.reduce((obj, cur) => ({ ...obj, [cur.name]: cur.value }), {})
      setDisabledFields(clientData, defaultSelectedSchema)
      setEnumFields(enums, defaultSelectedSchema)
      formDataRef.current = {
        ...formData,
        individual: additionalData?.individual || {},
        legal: additionalData?.legal || [],
      }
      setMatchedJSONSchema(defaultSelectedSchema)
      setClientType(getClientType(formData?.customer_type))
    }
    setPageErrors([])
    setIsDataLoading(false)
  }, [id])

  useEffect(() => {
    getData().catch(e => {
      console.error({e})
    })
  }, [getData])

  const onFormData = useCallback(({ formData }) => {
    formDataRef.current = formData
    if (formData?.customer_type) {
      setClientType(getClientType(formData?.customer_type))
    }
  }, [])

  const createOrder = useCallback(async () => {
    await fetchAPI(`/api/create-appeal-by-customer/${id}`)
      .then(result => {
        if (result?.errors) {
          Modal.error({
            title: 'Ошибка',
            content: <>
              При выполнении операции возникла ошибка:<br/><br/>
              {result.errors.join('\n')}
            </>,
          })
          return
        }
        if (result.id) {
          navigate(`/appeal/${result.id}`)
        }
      })
      .catch(err => {
        Modal.error({
          title: 'Ошибка',
          content: <>
            Проверьте подключение к интернету и повторите попытку<br /><br />
            {err.join('\n')}
          </>,
        })
      })
  }, [navigate, id])

  const checkUnfinished = useCallback(async () => {
    const unfinished = await fetchAPI(`/api/case/get-unfinished/${id}`)
    if (unfinished && Boolean(unfinished?.id)) {
      const caseId = unfinished?.id
      const cardName = unfinished?.card === 'order' ? 'заказ' : 'обращение'
      const cardUrl = unfinished?.card === 'order' ? `/order/${caseId}` : `/appeal/${caseId}`
      return Modal.confirm({
        title: 'Внимание!',
        content: (<>
          У пользователя есть незавершенн{cardName === 'заказ' ? 'ый' : 'ое'} {cardName} <b>№{unfinished.id}</b><br/>
          <a href={cardUrl} target='_blank'>Открыть {cardName}</a>
        </>),
        okText: 'Создать заказ',
        cancelText: 'Назад',
        onOk: () => {createOrder()},
      })
    } else {
      await createOrder()
    }
  },[id, createOrder])

  const warningMessage = <>
    Все контакты и обращения, заказы, замеры, монтажи, если таковые имеются будут переданы выбранному контакту.<br/>
    Действие невозможно отменить.<br/>
    После подтверждения действия вы будете переадресованы в объединенную карточку.
  </>

  const onIndividualDataChange = useCallback(individualData => {
    formDataRef.current.individual = {
      ...individualData
    }
  }, [])

  const individualData = {
    birthday: formDataRef.current?.individual?.birthday,
    passport_number: formDataRef.current?.individual?.passport_number,
    passport_issued: formDataRef.current?.individual?.passport_issued,
    registration_address: formDataRef.current?.individual?.registration_address,
  }

  const legalData = useMemo(() => {
    return formDataRef.current?.legal || []
  }, [formDataRef.current?.legal])

  const onLegalDataChange = useCallback(legalData => {
    formDataRef.current.legal = legalData
  }, [])

  const editMode = (val: boolean) => {
    setIsEdit(val)
  }

  const tabItems = useMemo<TabsProps['items']>(() => {
    const items: TabsProps['items'] = [
      {
        key: 'history',
        label: 'История заказов',
        children: <TabGeneral clientId={id}/>,
      },
      {
        key: 'call_log',
        label: 'Журнал звонков',
        children: <TableCallLog clientId={id}/>,
      },
      {
        key: 'contacts',
        label: 'Дополнительные контакты',
        children: <TableContacts userId={id}/>,
      },
    ]
    if (clientType === 'individual') {
      items.push({
        key: 'individual_data',
        label: 'Данные физического лица',
        children: <TabIndividual individualData={individualData} onChange={onIndividualDataChange} />,
      })
    }
    if (clientType === 'legal') {
      items.push({
        key: 'legal_data',
        label: 'Банковские реквизиты',
        children: <LegalTab legalData={legalData} onChange={onLegalDataChange} editMode={editMode} customerId={id} />,
      })
    }
    return items
  }, [clientType])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <MapWrapper>
        <TaskWrapper>
          <MapTitle>Клиент <span>ID:{id}</span></MapTitle>
          <Space size={'small'}>
            <MargeModal
              customerId={id}
              disabled={false}
              warningContent={warningMessage}
            />
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={checkUnfinished}
            >
              Создать заказ
            </Button>
          </Space>
        </TaskWrapper>
        <MapContent>
          <Form { ...formItemLayout } layout='horizontal' form={form} scrollToFirstError={true}>
            {pageErrors.length > 0 ? (
              <AlertWrapper>
                <Alert
                  message='При выполнении операции возникла ошибка:'
                  showIcon
                  type='error'
                  description={pageErrors.join('. ')}
                />
              </AlertWrapper>
            ) : null}
            {pageSuccess.length > 0 ? (
              <div style={{ marginBottom: 40 }}>
                <Alert
                  message={pageSuccess.join('. ')}
                  showIcon
                  type='success'
                />
              </div>
            ) : null}
            {!isDataLoading && matchedJSONSchema ? (
              <DefaultJsonForm
                formData={formDataRef.current}
                schema={matchedJSONSchema}
                onChange={onFormData}
                orientation='horizontal'
              />
            ) : (
              <Skeleton active/>
            )}
          </Form>
          <Tabs
            type='card'
            style={{ overflow: 'visible' }}
            items={tabItems}
          />
          <Button
            onClick={saveClientData}
            style={{ marginBottom: 16, marginTop: 16 }}
            type='primary'
            disabled={isEdit}
          >
            <SaveOutlined />
            Сохранить
          </Button>
        </MapContent>
      </MapWrapper>
    </B2BLayout>
  )
}

